import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Check,
  Loader2,
  Lock,
  PackagePlus,
  Tag,
  TriangleAlert,
  Truck,
  User,
  Warehouse,
} from 'lucide-react';
import clsx from 'clsx';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';
import { useNotice } from '../../../../hooks/useNotice';
import { PackageCard } from './components/PackageCard';
import { CourierLabels } from './components/CourierLabels';
import { PickLineModal } from './modals/PickLineModal';
import { UnpickLineModal } from './modals/UnpickLineModal';
import { LabelReadyModal } from './modals/LabelReadyModal';
import {
  addPackage,
  dispatchOrder,
  fetchFulfillment,
  labelDelivery,
  removePackage,
  reserveOrder,
} from '../../../../features/fulfillment/fulfillmentThunks';
import {
  clearFulfillment,
  labelResultDismissed,
  labelWaitGaveUp,
} from '../../../../features/fulfillment/fulfillmentSlice';
import {
  LABEL_WAIT_MS,
  LABEL_WAIT_NOTICE,
  courierSummaryOf,
  currentStep,
  hasCourierAnswer,
  isPackingDone,
  isPickingDone,
  labelBlocker,
  labelErrorNotice,
  packingSummary,
  pendingOf,
  pickBlocker,
  reserveBlocker,
  reserveDoneNotice,
  shipBlocker,
  totalWeightKg,
} from '../../../../features/fulfillment/fulfillmentUtils';
import { fetchSlotOptions } from '../../../../features/slots/slotsThunks';
import { SELLABLE_ZONES } from '../../../../features/slots/slotsConstants';
import { isOpenState, orderTitle } from '../../../../features/wmsOrders/wmsOrdersUtils';
import { selectCanWms } from '../../../../features/auth/authSelectors';
import {
  formatQuantity,
  moduleInactiveHint,
  moduleInactiveLabel,
  wmsOrderStateColor,
  wmsOrderStateLabel,
} from '../../../../utils/inventoryLabels';

const ORDERS_PATH = '/warehouse/orders';

const selectCanFulfill = selectCanWms('order:fulfill');

const Note = ({ tone, icon: Icon, testId, children }) => (
  <div
    data-testid={testId}
    className={clsx(
      'flex items-start gap-3 rounded-xl px-4 py-3 text-sm text-ninja-dark dark:text-white border',
      tone === 'danger' && 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900',
      tone === 'warning' && 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-900',
      tone === 'info' && 'bg-ninja-greenLight/60 dark:bg-ninja-green/10 border-ninja-green/30'
    )}
  >
    <Icon
      size={16}
      className={clsx(
        'flex-shrink-0 mt-0.5',
        tone === 'danger' && 'text-danger',
        tone === 'warning' && 'text-warning',
        tone === 'info' && 'text-ninja-green'
      )}
    />
    <div className="space-y-1">{children}</div>
  </div>
);

/** The three steps across the top: where the order is in the floor work. */
const Steps = ({ current, finished }) => {
  const labels = ['Pickear', 'Armar bultos', 'Etiquetar y despachar'];
  return (
    <ol data-testid="fulfill-steps" className="flex flex-wrap items-center gap-x-6 gap-y-2">
      {labels.map((label, index) => {
        const done = finished || index < current;
        const active = !finished && index === current;
        return (
          <li
            key={label}
            data-testid="fulfill-step"
            data-state={done ? 'done' : active ? 'active' : 'pending'}
            className="flex items-center gap-2 text-sm"
          >
            <span
              className={clsx(
                'w-6 h-6 rounded-full grid place-items-center text-xs font-bold',
                done && 'bg-ninja-green text-white',
                active && 'bg-ninja-greenLight text-ninja-green ring-2 ring-ninja-green',
                !done && !active && 'bg-ninja-gray dark:bg-[#2d2d2d] text-text-muted'
              )}
            >
              {done ? <Check size={14} /> : index + 1}
            </span>
            <span
              className={clsx(
                'font-semibold',
                active ? 'text-ninja-dark dark:text-white' : 'text-text-secondary'
              )}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const StepHeader = ({ index, title, hint, done, children }) => (
  <div className="flex flex-wrap items-start gap-4">
    <div className="flex items-start gap-3 flex-1 min-w-[240px]">
      <span
        className={clsx(
          'w-6 h-6 rounded-full grid place-items-center text-xs font-bold flex-shrink-0 mt-0.5',
          done ? 'bg-ninja-green text-white' : 'bg-ninja-gray dark:bg-[#2d2d2d] text-text-muted'
        )}
      >
        {done ? <Check size={14} /> : index}
      </span>
      <div>
        <h2 className="text-lg font-bold text-ninja-dark dark:text-white">{title}</h2>
        <p className="text-sm text-text-secondary">{hint}</p>
      </div>
    </div>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

/**
 * `/warehouse/orders/:orderId/fulfill`: picking and packing one order.
 *
 * It is a screen of **task**, not of consultation, so it has its own address
 * instead of a dialog: the picker stands in front of the rack, the list can be
 * long, and coming back to it with the order half done is the normal case.
 *
 * The picking here takes units out of a concrete location and writes the
 * kardex. The three steps are apart because they have three different "done":
 * picking ends when nothing is missing, packing when every parcel has its
 * weight and dimensions, and the exit is two acts — the label prepares the
 * shipment at the courier, dispatching is that the courier took the boxes.
 * Only the last one takes units out of the inventory.
 */
export const WarehouseOrderFulfill = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    order,
    lines,
    packages,
    supplyOptions,
    loading,
    error,
    busyAction,
    labelWaiting,
    labelResult,
  } = useSelector((state) => state.fulfillment);
  const slotOptions = useSelector((state) => state.slots.options);
  const canFulfill = useSelector(selectCanFulfill);

  const [picking, setPicking] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [confirmingDispatch, setConfirmingDispatch] = useState(false);
  const setNotice = useNotice('fulfill-notice');

  const load = useCallback(() => dispatch(fetchFulfillment(orderId)), [dispatch, orderId]);

  useEffect(() => {
    if (!canFulfill) return undefined;
    load();
    return () => dispatch(clearFulfillment());
  }, [load, dispatch, canFulfill]);

  // Where picked units go back to: the shelves of the order's warehouse.
  const warehouseId = order?.warehouse?.id;
  useEffect(() => {
    if (warehouseId) dispatch(fetchSlotOptions(warehouseId));
  }, [dispatch, warehouseId]);

  // The courier answered over the socket: the order carries the tracking now,
  // and a refusal is said here — the dialog only opens on a success.
  useEffect(() => {
    if (!labelResult) return;
    load();
    if (labelResult.status !== 'completed') {
      setNotice(labelErrorNotice(labelResult));
      dispatch(labelResultDismissed());
    }
  }, [labelResult, load, dispatch, setNotice]);

  // Silence has a limit: past it the screen says so and reloads, because the
  // tracking may well be on the order already if only the socket is down.
  useEffect(() => {
    if (!labelWaiting) return undefined;
    const timer = setTimeout(() => {
      dispatch(labelWaitGaveUp());
      setNotice(LABEL_WAIT_NOTICE);
      load();
    }, LABEL_WAIT_MS);
    return () => clearTimeout(timer);
  }, [labelWaiting, dispatch, load, setNotice]);


  const detailPath = `${ORDERS_PATH}/${orderId}`;

  // Preparing is floor work. The client follows its orders from the list; in
  // here there is nothing it can do, and letting it in was teaching it buttons
  // the server refuses one by one.
  if (!canFulfill) {
    return (
      <div data-testid="fulfill-forbidden" className="space-y-6">
        <DetailBreadcrumb backLabel="Volver a Órdenes" to={ORDERS_PATH} current="Preparar" />
        <Note tone="info" icon={Lock} testId="fulfill-forbidden-note">
          <p>
            La preparación de pedidos la hace quien opera la bodega. Tú puedes seguir el estado de
            tus órdenes desde la lista.
          </p>
        </Note>
      </div>
    );
  }

  const isCurrent = Boolean(order) && String(order.id) === String(orderId);

  // A failed reload of the order on screen leaves it there, under a strip.
  if (error && !isCurrent) {
    return (
      <ErrorState title="No se pudo abrir la orden" description={error} onRetry={load}>
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate(ORDERS_PATH)}>
          Volver a Órdenes
        </Button>
      </ErrorState>
    );
  }

  if (loading || !isCurrent) {
    return (
      <div data-testid="fulfill-loading" className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-ninja-green animate-spin" />
      </div>
    );
  }

  const blocked = order.module_active === false;
  const left = !isOpenState(order.wms_state);
  const frozen = blocked || left;

  const pickingDone = isPickingDone(lines);
  const packingDone = isPackingDone(packages);
  const step = currentStep({ lines, packages });
  const exitBlocker = shipBlocker({ lines, packages });
  const labelProblem = labelBlocker({ order, lines, packages });
  const reserveProblem = reserveBlocker({ lines });
  const courierSummary = courierSummaryOf(order, packages);
  const destinations = slotOptions.filter((slot) => SELLABLE_ZONES.includes(slot.zone_type));

  const run = async (thunk, onOk) => {
    const action = await dispatch(thunk);
    if (action.meta.requestStatus === 'fulfilled') {
      setNotice(onOk(action.payload));
    } else {
      setNotice({ type: 'error', message: action.payload });
    }
  };

  const lineColumns = [
    {
      header: 'PRODUCTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[280px]">
          <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.sku}</span>
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.product_name}>
            {row.product_name}
          </span>
        </div>
      ),
    },
    {
      header: 'PEDIDO',
      cell: (row) => <span className="tabular-nums">{formatQuantity(row.quantity)}</span>,
    },
    {
      header: 'PICKEADO',
      cell: (row) => (
        <span className="font-semibold tabular-nums">{formatQuantity(row.prepared_quantity)}</span>
      ),
    },
    {
      header: 'FALTA',
      cell: (row) => {
        const pending = pendingOf(row);
        return (
          <span
            data-testid="line-pending"
            className={clsx('tabular-nums', pending > 0 ? 'text-warning font-semibold' : 'text-text-muted')}
          >
            {pending > 0 ? formatQuantity(pending) : '—'}
          </span>
        );
      },
    },
    {
      // The nearest location with stock; the picker walks there first.
      header: 'DÓNDE ESTÁ',
      cell: (row) => {
        const suggestion = row.suggestions?.[0];
        if (pendingOf(row) === 0) return <span className="text-text-muted">—</span>;
        if (!suggestion) {
          return (
            <span data-testid="line-no-stock" className="text-xs text-danger">
              Sin stock pickeable
            </span>
          );
        }
        const more = row.suggestions.length - 1;
        return (
          <div className="flex flex-col">
            <span data-testid="line-where" className="font-mono text-ninja-dark dark:text-white">
              {suggestion.slot_name}
            </span>
            <span className="text-xs text-text-muted mt-0.5">
              {formatQuantity(suggestion.available)} disponibles
              {more > 0 ? ` · ${more} ubicación${more === 1 ? '' : 'es'} más` : ''}
            </span>
          </div>
        );
      },
    },
    {
      header: 'ACCIONES',
      cell: (row) =>
        pendingOf(row) > 0 ? (
          // Disabled, it says what is missing: stock on a shelf, or the reservation.
          <Button
            variant="primary"
            size="sm"
            onClick={() => setPicking(row)}
            disabled={frozen || Boolean(pickBlocker(row))}
            title={pickBlocker(row) === 'Falta reservar' ? 'Reserva la orden antes de pickear' : undefined}
            data-testid="line-pick"
          >
            {pickBlocker(row) === 'Falta reservar' ? 'Falta reservar' : 'Pickear'}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setUndoing(row)}
            disabled={frozen || Number(row.prepared_quantity) === 0}
            data-testid="line-unpick"
          >
            Deshacer
          </Button>
        ),
    },
  ];

  return (
    <div data-testid="fulfill-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel={`Volver a ${orderTitle(order)}`} to={detailPath} current="Preparar" />

      {error && <StaleDataNotice testId="fulfill-stale-error" error={error} onRetry={load} />}

      <DetailHero
        media={<Truck size={36} />}
        title={`Preparar ${orderTitle(order)}`}
        badges={
          <ToneBadge tone={wmsOrderStateColor(order.wms_state)} testId="fulfill-state">
            {wmsOrderStateLabel(order.wms_state)}
          </ToneBadge>
        }
        meta={
          <>
            {order.company && (
              <span className="flex items-center gap-1.5">
                <Building2 size={14} /> {order.company.name}
              </span>
            )}
            {order.warehouse && (
              <span className="flex items-center gap-1.5">
                <Warehouse size={14} /> {order.warehouse.name}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <User size={14} /> {order.recipient}
            </span>
          </>
        }
        actions={
          // Disabled, it says what is missing — or that it is done.
          <Button
            variant="ghost"
            onClick={() => run(reserveOrder({ orderId: order.id }), reserveDoneNotice)}
            loading={busyAction === 'reserve'}
            loadingLabel="Reservando..."
            disabled={frozen || Boolean(reserveProblem)}
            title={
              reserveProblem === 'Reservada'
                ? 'Todo lo pendiente tiene su stock comprometido.'
                : 'Compromete el stock disponible para esta orden. No mueve mercadería.'
            }
            data-testid="fulfill-reserve"
          >
            {reserveProblem === 'Reservada' ? 'Reservada' : 'Reservar'}
          </Button>
        }
      />

      {blocked && (
        <Note tone="danger" icon={TriangleAlert} testId="fulfill-module-off">
          <p>
            En <strong>{order.warehouse?.name}</strong>, {order.company?.name}{' '}
            <strong>{moduleInactiveLabel(order.module_inactive_reason)}</strong>. No se puede
            preparar esta orden.
          </p>
          {moduleInactiveHint(order.module_inactive_reason) && (
            <p className="text-xs text-text-secondary">{moduleInactiveHint(order.module_inactive_reason)}</p>
          )}
        </Note>
      )}

      {left && !blocked && (
        <Note
          tone={order.wms_state === 'dispatched' || order.wms_state === 'delivered' ? 'info' : 'warning'}
          icon={order.wms_state === 'dispatched' || order.wms_state === 'delivered' ? Check : TriangleAlert}
          testId="fulfill-left"
        >
          <p>
            {order.wms_state === 'dispatched' || order.wms_state === 'delivered'
              ? 'Esta orden ya salió de la bodega: el stock salió del kardex. No queda nada por hacer acá.'
              : `Esta orden está ${wmsOrderStateLabel(order.wms_state).toLowerCase()}: no se prepara.`}
          </p>
        </Note>
      )}

      {!frozen && !order.processable && lines.length > 0 && (
        <Note tone="warning" icon={TriangleAlert} testId="fulfill-short-stock">
          <p>
            No alcanzó el stock para reservar todo lo pedido. Se puede pickear lo que hay; el resto
            se reserva solo cuando entre mercadería, o con "Reservar" si ya entró.
          </p>
        </Note>
      )}

      <Steps current={step} finished={order.wms_state === 'dispatched' || order.wms_state === 'delivered'} />

      {/* ---- Step 1 · Picking ---------------------------------------- */}
      <section
        data-testid="fulfill-picking"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border space-y-4"
      >
        <StepHeader
          index={1}
          title="Pickear"
          hint="Recoger de la ubicación y dejar en la mesa de preparación. Descuenta del kardex."
          done={lines.length > 0 && pickingDone}
        />
        {lines.length === 0 ? (
          <p data-testid="fulfill-no-lines" className="text-sm text-text-secondary">
            Esta orden no tiene líneas de producto, así que no hay nada que pickear: se arman sus
            bultos, se etiqueta y se despacha igual.
          </p>
        ) : (
          <DataTable
            columns={lineColumns}
            data={lines}
            totalItems={lines.length}
            currentPage={1}
            itemsPerPage={100}
            idAccessor="order_product_id"
            itemLabel="líneas"
          />
        )}
      </section>

      {/* ---- Step 2 · Parcels ---------------------------------------- */}
      <section
        data-testid="fulfill-packing"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border space-y-4"
      >
        <StepHeader
          index={2}
          title="Armar bultos"
          hint="Cada bulto necesita peso y las tres medidas; sin eso el courier no puede tarificar y el despacho se rechaza. Anota también con qué se armó: se descuenta de Insumos y se cobra al cliente."
          done={packingDone}
        >
          <Button
            variant="secondary"
            icon={PackagePlus}
            onClick={() =>
              run(addPackage({ orderId: order.id }), () => ({ type: 'success', message: 'Bulto agregado' }))
            }
            loading={busyAction === 'addPackage'}
            loadingLabel="Agregando..."
            disabled={frozen}
            data-testid="fulfill-add-package"
          >
            Agregar bulto
          </Button>
        </StepHeader>

        {lines.length > 0 && !pickingDone && (
          <p className="text-sm text-text-secondary">
            Todavía falta pickear. Puedes ir armando los bultos, pero el despacho va a exigir la
            orden completa.
          </p>
        )}

        {packages.length === 0 ? (
          <p data-testid="fulfill-no-packages" className="text-sm text-text-muted">
            Sin bultos todavía. Agrega uno por cada caja que salga.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                orderId={order.id}
                lines={lines}
                packages={packages}
                supplyOptions={supplyOptions}
                canRemove={packages.length > 1}
                disabled={frozen}
                onRemove={(packageId) =>
                  run(removePackage({ orderId: order.id, packageId }), (result) => ({
                    type: 'success',
                    // Taking out the one parcel still open can close the order.
                    message: result?.delivery_status_changed
                      ? 'Bulto quitado. Todos los que quedan están cerrados: el pedido está armado.'
                      : 'Bulto quitado',
                  }))
                }
                onDone={setNotice}
              />
            ))}
          </div>
        )}

        {packages.length > 0 && (
          <p data-testid="fulfill-packing-summary" className="text-sm text-text-secondary">
            {packingSummary(packages)} · Peso total {totalWeightKg(packages).toFixed(2)} kg
          </p>
        )}
      </section>

      {/* ---- Step 3 · Exit ------------------------------------------- */}
      <section
        data-testid="fulfill-exit"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border space-y-4"
      >
        <StepHeader
          index={3}
          title="Etiquetar y despachar"
          hint="Son dos actos distintos: etiquetar prepara el envío en el courier, despachar es que se llevó las cajas."
          done={order.wms_state === 'dispatched' || order.wms_state === 'delivered'}
        />

        <div className="flex flex-wrap items-start gap-4">
          {/* Once the courier answered, the answer stays here: the dialog is
              only the moment it landed. */}
          {hasCourierAnswer(courierSummary) ? (
            <div className="flex-1 min-w-[280px] max-w-2xl">
              <CourierLabels summary={courierSummary} testId="fulfill" />
            </div>
          ) : (
            <div>
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                Seguimiento
              </span>
              <p data-testid="fulfill-tracking" className="font-mono text-ninja-dark dark:text-white">
                Sin etiqueta
              </p>
            </div>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              icon={Tag}
              onClick={() =>
                run(labelDelivery({ orderId: order.id, deliveryId: order.delivery_id }), () => ({
                  type: 'success',
                  message: 'Enviado al courier. Te avisamos apenas responda.',
                }))
              }
              loading={busyAction === 'label' || labelWaiting}
              loadingLabel={labelWaiting ? 'Esperando al courier...' : 'Etiquetando...'}
              disabled={frozen || Boolean(labelProblem)}
              title={labelProblem || 'Empuja el envío al courier y trae número de seguimiento y etiqueta. La mercadería no se mueve.'}
              data-testid="fulfill-label"
            >
              {labelProblem === 'Ya tiene etiqueta' ? 'Etiquetada' : 'Etiquetar'}
            </Button>
            {/* Disabled, it says what is missing. */}
            <Button
              variant="primary"
              icon={Truck}
              onClick={() => setConfirmingDispatch(true)}
              loading={busyAction === 'dispatch'}
              loadingLabel="Despachando..."
              disabled={frozen || Boolean(exitBlocker)}
              title="El courier se llevó las cajas. Es lo único que descuenta del inventario."
              data-testid="fulfill-dispatch"
            >
              {(!left && exitBlocker) || 'Despachar'}
            </Button>
          </div>
        </div>
      </section>

      {/* Dispatching is irreversible for the inventory, so it is confirmed. */}
      <ConfirmDialog
        isOpen={confirmingDispatch}
        onClose={() => setConfirmingDispatch(false)}
        onConfirm={() => {
          setConfirmingDispatch(false);
          run(dispatchOrder({ orderId: order.id }), () => ({
            type: 'success',
            message: `Orden ${orderTitle(order)} despachada: el stock salió del kardex.`,
          }));
        }}
        title="¿El courier ya se llevó las cajas?"
        message="Al confirmar, las unidades salen del inventario. Es el momento en que la mercadería deja de estar bajo tu custodia, así que conviene apretarlo cuando ya salió y no antes."
        confirmLabel="Sí, se las llevó"
        loadingLabel="Despachando..."
        loading={busyAction === 'dispatch'}
        testId="fulfill-dispatch-dialog"
      />

      <LabelReadyModal
        result={labelResult?.status === 'completed' ? labelResult : null}
        onClose={() => dispatch(labelResultDismissed())}
      />

      <PickLineModal
        line={picking}
        orderId={order.id}
        onClose={() => setPicking(null)}
        onDone={setNotice}
      />

      <UnpickLineModal
        line={undoing}
        orderId={order.id}
        destinations={destinations}
        onClose={() => setUndoing(null)}
        onDone={setNotice}
      />

    </div>
  );
};
